import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useUserStore, useProfileStore, useQuestionnaireStore, useCaseStore } from '../stores'
import { Menu, ChevronRight, Search, FolderOpen, Shield, Lock, User, Globe, Send, X, Eye, Sparkles, FileText, Mic, MicOff, Plus, Check } from 'lucide-react'
import { useI18n } from '../i18n'
import { useQuestionnaireTranslation } from '../i18n/questionnaires/useQuestionnaireTranslation'
import LanguageSelector, { LanguageButton } from '../components/LanguageSelector'
import HamburgerMenu from '../components/HamburgerMenu'
import NotificationBellButton from '../components/NotificationBellButton'
import AppHeader from '../components/AppHeader'
import DiscoveryToast from '../components/DiscoveryToast'
import InsuranceCandidateSheet from '../components/InsuranceCandidateSheet'
import { useAgent } from '../components/AgentContext'
import OrellaChatAgent from '../components/agent/OrellaChatAgent'
import { notificationsAPI, profileAPI } from '../services/api'
import BottomSheet from '../components/BottomSheet'
import { useNotificationStore } from '../stores/useNotificationStore'
import TrustBar from '../components/TrustBar'
import useVoiceInput from '../hooks/useVoiceInput'
import orellaLogo from '../assets/logos/orella_46A8F9_logo.png'
import orellaOIcon from '../assets/logos/orella_o_icon.png'
import handIcon from '../assets/hand_icon.webp'
import { getSpecialtyIcon } from '../utils/specialtyIcons'
// UX-1 / TF-02 / TF-03 (v242.61.0): shared completeness, see profileCompletion.js
import { isSectionComplete, allRequiredComplete, REQUIRED_SECTIONS, missingBeforeShare } from '../utils/profileCompletion'
import { getStatusBadge } from '../utils/caseBadges'
import { formatDate } from '../utils/dateUtils'
import { postLoginResume, POST_LOGIN_KEY } from '../utils/postLoginTarget'
import { discoverySnoozeKey } from '../utils/insuranceDiscovery'
import { INSURANCE_DISCOVERY_ENABLED } from '../config/featureFlags'
import { resolveSpecialtyId, formatSpecialtyName, formatCaseSpecialty } from '../utils/specialtyResolver'
import { clinicianFormsFor } from '../utils/clinicianName.js'

// ★ v242.234.0 (Build 59) — THE HOME TEASER STRIP IS GONE, by Dr. Rami's ruling on mockup B
//   (claude/ORELLA_HOME_CASE_MOCKUP_B_2026-10-04.md): Home keeps its header and the Orella
//   dialogue, then ONE "Start a new case" banner whose only door is "All areas". The six
//   area pills and the General card both left Home; General concern now leads the browse
//   screen (SpecialtyBrowseScreen, GENERAL_AREA_ID), which is the one place a case starts.
//   ARM TS-1 of check-specialty-resolver moved with it and is now ARM GC-1.

// ★ PROD-936 PAT-1 / PROD-927 (Alex 2026-10-13, decision 3): NO Home 90-day re-check. The v242.7.0
//   quarterly re-discovery (a paid $1.50 Stedi probe on the first Home visit, also for a patient who
//   never ran one) is deleted, not switched: it does not come back with INSURANCE_DISCOVERY_ENABLED.
//   scripts/check-no-patient-discovery.mjs holds it.

// ★ INSURE-2a (P12): the material-change rule moved to utils/insuranceDiscovery.js, the one
//   owner the COB sheet uses too. The local copy returned false whenever the applied plan was
//   still active, so a SECOND active plan that appeared later was never offered.

export default function HomeScreen() {
  const navigate = useNavigate()
  const location = useLocation()
  
  // Auto-open hamburger if navigated back from a sub-page
  useEffect(() => {
    if (location.state?.openMenu) {
      setMenuOpen(true)
      window.history.replaceState({}, '')
    }
  }, [location.state])

  const user = useUserStore((state) => state.user)
  const profile = useProfileStore((state) => state.profile)
  const applyDiscoveryResult = useProfileStore((state) => state.applyDiscoveryResult)
  const applyDiscoverySecondary = useProfileStore((state) => state.applyDiscoverySecondary)
  const dismissDiscoveryResult = useProfileStore((state) => state.dismissDiscoveryResult)
  const dismissDiscoveryToast = useProfileStore((state) => state.dismissDiscoveryToast)
  const discoveryToastSnoozedFor = useProfileStore((state) => state.discoveryToastSnoozedFor)
  const updateProfile = useProfileStore((state) => state.updateProfile)

  // v228.0.0 — pending-friendly discovery on the app: when onboarding left an
  // in-flight (PENDING) discovery, poll it live while Home is open so the
  // toast surfaces as soon as Stedi finishes — instead of waiting for the next app
  // open. The effect keys on the status string: it stops itself the moment status leaves
  // 'pending', and the closure's attempt counter caps the loop.
  // ★ PROD-936 PAT-1 (F2): it READS THE PROFILE (GET /profile, free), never POST
  //   .../insurance-discovery. The sign-up job's marker carries no discoveryId, so that POST did not
  //   resume the running job: it bought a fresh paid search while the job still ran.
  const discoveryStatus = profile?.insurance?.discovery_result?.status
  const userId = user?.id
  useEffect(() => {
    if (!INSURANCE_DISCOVERY_ENABLED || !userId || discoveryStatus !== 'pending') return
    let cancelled = false
    let timer = null
    let attempts = 0
    const POLL_MAX = 5
    const poll = async () => {
      if (cancelled) return
      attempts += 1
      try {
        const fresh = await profileAPI.get()
        const d = fresh?.insurance?.discovery_result
        if (!cancelled && d) {
          const cur = useProfileStore.getState().profile?.insurance || {}
          updateProfile({ insurance: { ...cur, discovery_result: d } })
          if (d.status === 'pending' && attempts < POLL_MAX) timer = setTimeout(poll, 8000)
        } else if (!cancelled && attempts < POLL_MAX) {
          timer = setTimeout(poll, 8000)
        }
      } catch {
        if (!cancelled && attempts < POLL_MAX) timer = setTimeout(poll, 8000)
      }
    }
    timer = setTimeout(poll, 4000) // brief delay after Home mounts
    return () => { cancelled = true; if (timer) clearTimeout(timer) }
  }, [userId, discoveryStatus, updateProfile])

  // PROD-936 PAT-1 / PROD-927: the v242.7.0 90-day re-discovery effect that stood here is deleted (see
  //   the note at the top of the file). The sheet below still opens on a stored discovery result.
  const [insuranceSheetOpen, setInsuranceSheetOpen] = useState(false)
  const { specialties } = useQuestionnaireStore()
  const cases = useCaseStore((s) => s.cases)
  const setCurrentCase = useCaseStore((s) => s.setCurrentCase)
  const { tfa } = useQuestionnaireTranslation()
  const [menuOpen, setMenuOpen] = useState(false)
  const [languageOpen, setLanguageOpen] = useState(false)
  const [triageInput, setTriageInput] = useState('')
  const [trustSheet, setTrustSheet] = useState(null) // 'hipaa' | 'data' | null


  // v223.3.0 (F1): unread doctor-message notices for the hero. Derived from
  // notifications (mirrors CasesListScreen), grouped per case. The full message
  // renders on the case page; this is just a pointer.
  const [mdNotices, setMdNotices] = useState([])
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const result = await notificationsAPI.list()
        if (cancelled) return
        const byCase = {}
        ;(result.notifications || []).forEach((n) => {
          if (n.read) return
          if (n.type !== 'case_message') return
          if (n.data?.direction && n.data.direction !== 'from_md') return
          const cid = n.data?.caseId
          if (!cid) return
          if (!byCase[cid]) byCase[cid] = { caseId: cid, doctorName: n.data?.doctorName || '', doctorTitle: n.data?.doctorTitle || '', count: 0, notifIds: [], ts: 0 }
          byCase[cid].count += 1
          byCase[cid].notifIds.push(n.id)
          const ts = new Date(n.created_at || 0).getTime()
          if (ts > byCase[cid].ts) byCase[cid].ts = ts
        })
        setMdNotices(Object.values(byCase).sort((a, b) => b.ts - a.ts))
      } catch (e) { /* silent — notices are non-critical */ }
    }
    load()
    const onVis = () => { if (document.visibilityState === 'visible') load() }
    document.addEventListener('visibilitychange', onVis)
    return () => { cancelled = true; document.removeEventListener('visibilitychange', onVis) }
  }, [])

  // v223.3.0 (F4): resume a captured case deep link after sign-in. Every sign-in
  // success funnels through /home, so consuming it here covers all login paths.
  useEffect(() => {
    try {
      // ★ PROD-38 (P3): the stored route is checked again before it is followed.
      const r = sessionStorage.getItem(POST_LOGIN_KEY)
      if (r) { sessionStorage.removeItem(POST_LOGIN_KEY); const to = postLoginResume(r); if (to) navigate(to, { replace: true }) }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Resolve "Specialty · Focus Area" label for a notice's case (from loaded cases)
  const caseLabelFor = (caseId) => {
    const c = (cases || []).find((x) => x.id === caseId || x.caseId === caseId)
    if (!c) return ''
    const specName = formatCaseSpecialty(c, t)
    const faRaw = c.focusAreaName || c.focusArea?.name || ''
    const fa = faRaw ? tfa(faRaw) : ''
    return fa ? `${specName} · ${fa}` : specName
  }

  // Tap a notice → mark its MD messages read, then open that case's chat
  const openMdNotice = async (notice) => {
    try {
      for (const id of notice.notifIds) {
        await notificationsAPI.markRead(id)
        useNotificationStore.getState().decrementUnread()
      }
    } catch (e) { /* best-effort */ }
    setMdNotices((prev) => prev.filter((n) => n.caseId !== notice.caseId))
    const c = (cases || []).find((x) => x.id === notice.caseId || x.caseId === notice.caseId)
    if (c) setCurrentCase(c)
    navigate(`/case/${notice.caseId}?openChat=1`)
  }

  // Animation states — only animate on first visit per session
  const homeAnimPlayed = sessionStorage.getItem('home_anim_played')
  const [showContent, setShowContent] = useState(!!homeAnimPlayed)
  const [bubblePhase, setBubblePhase] = useState(homeAnimPlayed ? 'text' : 'dots') // dots → text

  const { openAgent, homeSessionActive, startHomeSession, agentCaseId, agentCaseContext } = useAgent()
  const { t, language } = useI18n()

  // v235.6.0: open the Learn-more sheet for a specialty area and fetch its

  // Voice-to-text for the search input — live streaming
  const { isListening, isTranscribing, interim: voiceInterim, toggle: voiceToggle, error: voiceError, clearError: clearVoiceError, mode: voiceMode } = useVoiceInput({
    onTranscript: (text) => setTriageInput(text),
    language,
    maxDuration: 30,
  })
  
  const firstName = user?.firstName || profile?.firstName || 'there'

  // Time-based greeting
  const getGreeting = () => {
    const hour = new Date().getHours()
    const name = firstName !== 'there' ? `, ${firstName}` : ''
    if (hour < 12) return `${t('home.goodMorning')}${name}!`
    if (hour < 17) return `${t('home.goodAfternoon')}${name}!`
    return `${t('home.goodEvening')}${name}!`
  }

  // Profile completion detection — v242.61.0 (UX-1): this was a third verbatim
  // copy of the section checks. The chip below decides whether to nag the patient,
  // so it MUST agree with the profile headline; now it does by construction.
  const hasIdentification = isSectionComplete('identification', profile)
  const hasInsurance = isSectionComplete('insurance', profile)
  const hasAllergies = isSectionComplete('allergies', profile)
  const hasMedications = isSectionComplete('medications', profile)
  const requiredProfileComplete = allRequiredComplete(profile)
  const profileOnboardingDismissed = localStorage.getItem('orella_profile_onboarding_dismissed') === 'true'
  const showProfileChip = !requiredProfileComplete && !profileOnboardingDismissed
  // v242.234.0 — the health-profile banner counts what the share gate counts, from the
  //   same owner (missingBeforeShare), so the banner and the before-share step never disagree.
  const profileMissing = missingBeforeShare(profile)
  const profileDone = REQUIRED_SECTIONS.length - profileMissing.length
  const profileRoute = profileMissing.includes('identification') ? '/profile?guided=true' : '/onboarding?run=profile'

  // v145.1.0: Check if user needs 2FA migration
  useEffect(() => {
    const dismissed = sessionStorage.getItem('orella_2fa_dismissed')
    if (dismissed) return
    if (user?.needs2faSetup) {
      navigate('/2fa-upgrade', { replace: true })
    }
  }, [user?.needs2faSetup])

  // Entrance animation
  useEffect(() => {
    if (homeAnimPlayed) return
    const t1 = setTimeout(() => { setBubblePhase('text') }, 1000)
    const t2 = setTimeout(() => { 
      setShowContent(true)
      sessionStorage.setItem('home_anim_played', 'true')
    }, 1300)
    return () => { clearTimeout(t1); clearTimeout(t2) }
  }, [])

  // Lock body scroll when menu is open
  useEffect(() => {
    if (menuOpen) { document.body.style.overflow = 'hidden' }
    else { document.body.style.overflow = '' }
    return () => { document.body.style.overflow = '' }
  }, [menuOpen])

  // v242.81.0 (TF-39) — body-scroll lock removed: BottomSheet owns it, and
  // does it properly (position:fixed, which iOS needs). Two writers to
  // document.body.style.overflow means whichever cleanup runs last wins.
  
  // Handle sending message to agent
  // v206.2: Bottom sheet (not fullscreen) + start home chat session so the bubble
  // collapses to a "Continue our chat" pill on subsequent visits this session.
  const handleSendToAgent = () => {
    if (!triageInput.trim()) return
    const trimmed = triageInput.trim()
    // v213.10: Persist the triage input so newly-created cases can seed it as
    // voiceTextInput. Only the FIRST message in a session seeds — subsequent
    // home-box messages don't overwrite. The seed is consumed by DescribeVoice
    // on case creation.
    try {
      const existingSeed = sessionStorage.getItem('orella_home_triage_input')
      if (!existingSeed) {
        sessionStorage.setItem('orella_home_triage_input', trimmed)
        sessionStorage.setItem('orella_home_triage_ts', String(Date.now()))
      }
    } catch {}
    startHomeSession()
    openAgent(trimmed, 'home_search')
    setTriageInput('')
  }

  // v206.2: Reopen the home chat session (bottom sheet) without a new message
  const handleResumeChat = () => {
    openAgent(null, 'home_pill')
  }

  // Trust bar tap handler
  const handleTrustTap = (target) => {
    if (target === 'language') {
      setLanguageOpen(true)
    } else if (target === 'hipaa' || target === 'data') {
      setTrustSheet(target)
    }
  }

  // v242.87.0 (TF b34 #1) — "Edges of home banner dont quite reach the margin
  // flush." The `max(..., 4px)` floor meant that in PORTRAIT, where
  // env(safe-area-inset-left/right) is 0px, this resolved to a hard 4px — a
  // white rail down both sides of the greeting band. It is the only occurrence
  // in the codebase: Cases, Visits and Profile mount the same AppHeader with no
  // such wrapper, so their bands are already edge-to-edge and Home was the
  // outlier. Dropping the floor keeps the landscape-notch protection, which is
  // the only thing it was ever needed for. Body content below carries its own
  // gutter (px-5), so nothing else shifts.
  // ★ THIS COMMENT WAS A `{/* … */}` BLOCK IMMEDIATELY AFTER `return (` AND IT
  //   DID NOT PARSE. A JSX comment goes in CHILDREN position only — never as a
  //   sibling of the root inside `return (`. ORELLA_RULES §0(3) says exactly
  //   this, in bold, and I wrote it anyway. scripts/check-parse.mjs caught it
  //   on its first run in this repo, which is the argument for the gate.
  // PROD-306: the toast is given nothing while the patient has closed it for THIS result
  // this session (a second-plan offer is snoozed, never acknowledged; see
  // dismissDiscoveryToast). A newer result carries another stamp and is not hidden by it.
  const toastDiscovery = (() => {
    const dr = profile?.insurance?.discovery_result
    if (dr && discoveryToastSnoozedFor && discoveryToastSnoozedFor === discoverySnoozeKey(dr)) return null
    return dr
  })()

  return (
    <div className="min-h-full bg-white relative" style={{ paddingLeft: 'env(safe-area-inset-left, 0px)', paddingRight: 'env(safe-area-inset-right, 0px)' }}>
      {/* v222.1.0 Stedi — Insurance Discovery toast (only renders when there's a pending result) */}
      {/* v242.7.0 (D) — discovery nudge escalates into the full pick-list sheet */}
      <DiscoveryToast
        discoveryResult={toastDiscovery}
        onReview={() => setInsuranceSheetOpen(true)}
        onDismiss={() => { dismissDiscoveryToast() }}
      />
      <InsuranceCandidateSheet
        open={insuranceSheetOpen}
        discoveryResult={profile?.insurance?.discovery_result}
        onApplyPrimary={async (idx) => await applyDiscoveryResult(idx)}
        onApplySecondary={async (idx) => await applyDiscoverySecondary(idx, userId)}
        onDismiss={() => { setInsuranceSheetOpen(false); dismissDiscoveryResult() }}
        onManual={() => { setInsuranceSheetOpen(false); dismissDiscoveryResult(); navigate('/profile/insurance') }}
        onClose={() => { setInsuranceSheetOpen(false) }}
      />

      {/* Animation keyframes */}
      <style>{`
        @keyframes homeFadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>

      {/* v242.76.0 APPHEADER-1B-1 — Home's bar, greeting and trust row are the
          title band now, so every tab shares one shell. Home is not a variant
          of the others; the others are variants of Home, which is why its
          greeting simply occupies the band's slot. */}
      <AppHeader
        variant="home"
        greeting={getGreeting()}
        greetingAdornment={<img src={handIcon} alt="" className="w-7 h-7 object-contain wave-hand" />}
        onTrustTap={handleTrustTap}
        onMenu={() => setMenuOpen(true)}
        onLanguage={() => setLanguageOpen(true)}
      />

      {/* ═══════════════════════════════════════════
          ORELLA DIALOGUE — inline chat in the page body (v223.5.0)
          The hero IS the agent: greeting + thread + input render inline and
          the page scrolls; no overlay. MD notices sit between thread and input.
          ═══════════════════════════════════════════ */}
      {/* v242.78.0 (TF-32) — the dialogue was flush against the title band. */}
      <div className="px-5 pt-4 pb-4">
        {/* s120 round 2 (canvas "Orella App Chat — Round 2" H1, approved): Orella's latest line + one navy "Ask me
            anything" pill (mic opens it recording); the conversation itself is the bottom drawer. */}
        <OrellaChatAgent
          inline
          isOpen
          composer="open"
          caseId={agentCaseId}
          caseContext={agentCaseContext}
          onNavigate={navigate}
          interstitial={
            mdNotices.length > 0 ? (
              <div className="mt-3 space-y-2">
                {mdNotices.slice(0, 3).map((notice) => {
                  const label = caseLabelFor(notice.caseId)
                  const headline = notice.count > 1
                    ? t('home.mdMessagePlural', { ...clinicianFormsFor(notice.doctorTitle, notice.doctorName), name: notice.doctorName || '', count: notice.count })
                    : t('home.mdMessageSingle', { ...clinicianFormsFor(notice.doctorTitle, notice.doctorName), name: notice.doctorName || '' })
                  return (
                    <button
                      key={notice.caseId}
                      onClick={() => openMdNotice(notice)}
                      className="w-full flex items-center gap-3 rounded-[14px] px-3 py-2.5 active:scale-[0.99] transition-transform"
                      style={{ background: '#E9FAEE', border: '1px solid #B8ECC4' }}
                    >
                      <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-[13px] font-extrabold" style={{ background: '#66D170', color: '#0c3d18' }}>
                        {(notice.doctorName || 'D').charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0 text-left">
                        <div className="flex items-center gap-2">
                          <span className="text-[13.5px] font-bold truncate" style={{ color: '#15803d' }}>{headline}</span>
                          <span className="text-[9px] font-extrabold px-2 py-0.5 rounded-full flex-shrink-0" style={{ background: '#1a8c3a', color: '#fff', letterSpacing: '0.4px' }}>NEW</span>
                        </div>
                        <div className="text-[12px] font-medium truncate" style={{ color: '#3f6b4c' }}>
                          {t('home.mdMessageTapToReply')}{label ? ` · ${label}` : ''}
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: '#15803d' }} />
                    </button>
                  )
                })}
                {mdNotices.length > 3 && (
                  <button onClick={() => navigate('/cases')} className="w-full text-left px-3 py-1.5 text-[12.5px] font-bold" style={{ color: '#15803d' }}>
                    {t('home.mdMessageMore', { count: mdNotices.length - 3 })}
                  </button>
                )}
              </div>
            ) : null
          }
        />
      </div>

      {/* ═══════════════════════════════════════════
          START A NEW CASE — v242.234.0 (Build 59, mockup B v3)
          One banner, one door: the whole card opens the browse screen, where
          General concern leads and every area follows. The "All areas" pill is
          a label inside the button, not a second button (no nested buttons).
          The health-profile banner sits under it, light green — Dr. Rami's
          ruling: the navy version read too dark.
          ═══════════════════════════════════════════ */}
      <div className={`px-5 pt-2 transition-all duration-500 ${showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`}>
        <button
          onClick={() => navigate('/specialties')}
          className="w-full flex flex-col items-start gap-3 px-[15px] py-3.5 rounded-[18px] bg-white text-left active:scale-[0.99] transition-all breathe-ring"
          style={{
            border: '1.5px solid #A7D9FC',
            '--ring-color': 'rgba(70, 168, 249, 0.45)',
            '--ring-fade': 'rgba(70, 168, 249, 0)',
            '--ring-spread': '10px',
          }}
        >
          <span className="flex items-center gap-3 w-full">
            <span className="w-[46px] h-[46px] rounded-[13px] bg-[#E9F8FF] border border-[#A7D9FC] flex items-center justify-center flex-shrink-0">
              <Plus className="w-6 h-6 text-[#46A8F9]" strokeWidth={2.4} />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-[17px] font-extrabold text-[#15354F] tracking-[-0.15px]">
                {t('home.startNewCase', 'Start a new case')}
              </span>
              <span className="block text-[13px] text-[#5F7A8E] font-medium mt-0.5 leading-snug">
                {t('home.startCaseBannerSub', "Tell us what's going on. We'll organize it for your doctor.")}
              </span>
            </span>
          </span>
          <span
            className="inline-flex items-center px-4 py-2 rounded-full text-[14px] font-extrabold text-[#46A8F9]"
            style={{ border: '1px solid #CFE6FB', background: '#F2F9FE' }}
          >
            {t('home.allAreas', 'All areas')}
            <ChevronRight className="w-4 h-4 ml-0.5 flex-shrink-0" />
          </span>
        </button>

        {showProfileChip && (
          <button
            onClick={() => navigate(profileRoute)}
            className="w-full mt-3 flex flex-col items-start gap-2 px-[14px] py-3 rounded-[18px] text-left active:scale-[0.99] transition-transform"
            style={{ background: '#F6FBF6', border: '1.5px solid #CDEBCB' }}
          >
            <span className="flex items-baseline justify-between w-full gap-2">
              <span className="text-[15px] font-extrabold text-[#15354F]">
                {t('home.finishHealthProfile', 'Finish your health profile')}
              </span>
              <span className="text-[12.5px] font-bold text-[#1D6B26] whitespace-nowrap">
                {t('home.profileDoneCount', { defaultValue: '{{done}} of {{total}}', done: profileDone, total: REQUIRED_SECTIONS.length })}
              </span>
            </span>
            <span className="text-[13px] text-[#4A5A6B] leading-snug">
              {t('home.neededBeforeShare', 'Needed before you share a case with a doctor.')}
            </span>
            <span className="block h-1.5 w-full rounded-full overflow-hidden" style={{ background: '#E2F0E1' }}>
              <span className="block h-full rounded-full" style={{ width: `${Math.round((profileDone / REQUIRED_SECTIONS.length) * 100)}%`, background: '#66D170' }} />
            </span>
            <span className="flex flex-wrap gap-1.5">
              {REQUIRED_SECTIONS.map((id) => {
                const done = !profileMissing.includes(id)
                return (
                  <span
                    key={id}
                    className="inline-flex items-center gap-1 text-[12px] font-bold rounded-full px-2.5 py-1"
                    style={done
                      ? { background: '#D5EFD3', color: '#1D6B26', border: '1px solid #D5EFD3' }
                      : { background: '#FFFFFF', color: '#15354F', border: '1px solid #DDEBDD' }}
                  >
                    {done && <Check size={12} strokeWidth={3} />}
                    {t(`profile.${id}`)}
                  </span>
                )
              })}
            </span>
            <span className="inline-flex items-center text-[13.5px] font-extrabold text-[#46A8F9]">
              {t('home.continueProfile', 'Continue')}
              <ChevronRight className="w-4 h-4 ml-0.5 flex-shrink-0" />
            </span>
          </button>
        )}
      </div>

      {/* ═══════════════════════════════════════════
          CONTINUE PRIOR CASE — v206.0 V3 card
          Shows most recent active case with status badge from caseBadges util.
          Empty state = thinner translucent pill linking to /cases.
          ═══════════════════════════════════════════ */}
      {(() => {
        // Filter to active (non-completed/closed/archived) and sort newest first
        // ★ s101 P1.4 (Alex, Build 65 x_16: "It showed an older case. It should show the newest"):
        //   a finalized case in FOLLOW-UP or OBSERVATION is still the patient's live case (care plan,
        //   next visit), so it is no longer filtered out — only completed and archived cases are.
        //   Newest by last activity (updatedAt), then by creation.
        const activeCases = (cases || [])
          .filter(c => !['completed', 'archived'].includes(c.status))
          .sort((a, b) => new Date(b.updatedAt || b.updated_at || b.createdAt || 0) - new Date(a.updatedAt || a.updated_at || a.createdAt || 0))
        const recent = activeCases[0]
        const totalCount = activeCases.length

        // Mirrors CasesListScreen.handleCaseClick — keeps behavior identical
        const handleOpenCase = (caseItem) => {
          setCurrentCase(caseItem)
          const isMDInitiated = caseItem.doctor || caseItem.doctor_id || caseItem.sentToDoctor ||
            (caseItem.status === 'claimed' && !caseItem.describeComplete)
          const hasOnboarded = caseItem.patientAgreement?.signed || caseItem.onboardingComplete
          if (isMDInitiated && !hasOnboarded) {
            navigate('/case/share', { state: { caseId: caseItem.id, mdOnboard: true } })
          } else {
            navigate('/case/home', { state: { returnTo: '/home' } })
          }
        }

        // Empty state — thinner translucent pill
        if (!recent) {
          return (
            <div className={`px-5 mt-3 transition-all duration-500 ${showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`}>
              <button
                onClick={() => navigate('/cases')}
                className="w-full inline-flex items-center justify-between px-4 py-2.5 rounded-full bg-white active:scale-[0.98] transition-all"
                style={{
                  border: '1.25px solid #CBD5E1',
                  opacity: 0.65,
                }}
              >
                <div className="flex items-center gap-2">
                  <FolderOpen className="w-4 h-4 text-[#15354F]" style={{ opacity: 0.7 }} />
                  <span className="text-[12.5px] font-bold text-[#15354F]">{t('home.viewYourCases')}</span>
                </div>
              </button>
            </div>
          )
        }

        // V3 card — recent case preview (v206.1: uses shared specialtyResolver)
        const rawSpecialty = recent.specialtyId || recent.specialty?.id || recent.specialtyName || recent.specialty?.name
        const canonicalId = resolveSpecialtyId(rawSpecialty)
        const specialtyName = formatCaseSpecialty(recent, t)
        const focusAreaRaw = recent.focusAreaName || recent.focusArea?.name || ''
        const focusAreaName = focusAreaRaw ? tfa(focusAreaRaw) : ''
        const iconSrc = getSpecialtyIcon({ id: canonicalId, name: specialtyName })
        const badge = getStatusBadge(recent, t)

        // ★ v242.162.0 — THE NAVY-OUTLINED CARD IS GONE. A 1.75px navy box was the
        //   heaviest border on Home, sitting under a block that now has none, and
        //   it made the LAST thing on the screen look like the most important.
        //   This is the same eyebrow + hairline + flat row the block above uses,
        //   so Home reads as one list, not three competing card systems.
        // ★★ WHAT IT COSTS: the status badge loses its colour. getStatusBadge
        //    still owns the label — badge.bg / badge.color are simply no longer
        //    read HERE — so status arrives as grey text beside the date. If a
        //    status ever needs to shout on Home (overdue, action required), this
        //    is the row that grows a badge back; the util still has the colours.
        // ★★★ AND THIS COMMENT IS `//`, ABOVE `return (`, FOR A REASON: it was
        //     first written as a {/* … */} block in the line below `return (`,
        //     which is the ONE placement JSX does not allow — §0(3), in bold, in
        //     RULES, and in this file's own header comment 100 lines up. It cost
        //     a build. check-parse.mjs named the file and the column.
        return (
          <div className={`px-5 mt-6 transition-all duration-500 ${showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`}>
            <div className="flex items-baseline justify-between mb-2 pl-0.5">
              <span className="text-[11.5px] font-extrabold text-[#15354F] uppercase tracking-[0.085em]">
                {t('home.continueLabel')}
              </span>
              {totalCount > 1 && (
                <button
                  onClick={() => navigate('/cases')}
                  className="text-[13.5px] font-extrabold text-[#46A8F9] active:opacity-70 transition-opacity"
                >
                  {t('home.viewAllCases', { count: totalCount })}
                </button>
              )}
            </div>
            <div className="h-px" style={{ background: '#E6EDF3' }} />
            <button
              onClick={() => handleOpenCase(recent)}
              className="w-full flex items-center gap-3 py-3 px-0.5 rounded-xl active:bg-slate-50 transition-colors"
            >
              <div className="w-[44px] h-[44px] rounded-[12px] bg-[#F1F7FC] flex items-center justify-center flex-shrink-0">
                {iconSrc ? (
                  <img src={iconSrc} alt="" className="w-7 h-7 object-contain" />
                ) : (
                  <FolderOpen className="w-5 h-5 text-[#46A8F9]" />
                )}
              </div>
              <div className="flex-1 min-w-0 text-left">
                <p className="text-[16px] font-extrabold text-[#15354F] truncate">
                  {focusAreaName ? `${specialtyName} · ${focusAreaName}` : specialtyName}
                </p>
                <p className="text-[13px] text-[#8FA3B4] font-medium mt-0.5 truncate">
                  {recent.createdAt ? `${badge.label} · ${formatDate(recent.createdAt)}` : badge.label}
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-[#B9C7D4] flex-shrink-0" />
            </button>
          </div>
        )
      })()}

      {/* Spacer */}
      <div className="h-8" />

      {/* Hamburger Menu */}
      <HamburgerMenu isOpen={menuOpen} onClose={() => setMenuOpen(false)} />

      {/* Trust Info Bottom Sheet */}
      {/* ═══ Trust Sheets — v185.8 Redesign ═══ */}
      {/* v242.81.0 (TF-39) — "the bottom of the pop up is cut off".
          This sheet was a hand-rolled copy of BottomSheet, which THIS FILE
          already imports (line 16) and uses 150 lines below for the Learn-more
          sheet. The copy rendered its overlay INLINE, and every routed page is
          wrapped in PageWrapper (App.jsx) — a framer-motion motion.div. A
          transformed ancestor is a containing block for `position: fixed`, so
          the overlay was never viewport-fixed: it was anchored to PageWrapper's
          box, which is as tall as the scrollable page.

          Measured in a harness driven off the real built CSS, 390x844 viewport:

            ancestor transform:none    panel [389,844]  Close [775,820]   on screen
            ancestor translateY(0px)   panel [745,1200] Close [1131,1176] OFF screen
            ancestor will-change:tfm   panel [745,1200] Close [1131,1176] OFF screen
            ancestor opacity:0.99      panel [389,844]  Close [775,820]   on screen  (control)

          The Close button lands 300px below the bottom of the screen, and the
          tab bar is left undimmed on top — exactly the screenshot. NO z-index
          change fixes this; z-[100] over the bar's z-50 was already correct.
          BottomSheet portals to document.body, which escapes the containing
          block entirely, and brings 85dvh (not 85vh — it survives the iOS
          toolbar), the iOS position:fixed body-scroll lock, Escape-to-close and
          z-index 9998 with it. The inner content is unchanged apart from the
          safe-area bottom padding, so the TF-38 header treatment is untouched.

          ★ The keyframes at line ~400 are NOT removed with this markup even
          though nothing in this file uses them any more: VisitsScreen and
          FollowUpReportModal reference `fadeIn` and define it nowhere, so they
          animate only while a screen that defines it is mounted. Deleting it
          here would silently stop two other screens animating — RULES 1aq(c).
          Filed as KEYFRAMES-GLOBAL-LEAK-1. */}
      <BottomSheet open={!!trustSheet} onClose={() => setTrustSheet(null)}>
            {trustSheet === 'hipaa' && (
              <div className="px-4 pt-1 overflow-y-auto"
                   style={{ paddingBottom: '1.5rem' /* PROD-1176: the BottomSheet panel pays the bottom inset */ }}>
                {/* v242.80.0 (TF-38) — the sheet's own header, no longer a navy
                    slab. A dark gradient card inside a white sheet is a second
                    theme inside a surface that already has one, and it is the
                    exact treatment the 1B header work just removed from four
                    screens. Same structure, ice-blue ground, navy as TEXT. */}
                <div className="rounded-[14px] overflow-hidden mb-3 relative"
                     style={{ background: 'linear-gradient(to bottom, #F4FAFF, #EAF4FE)', border: '1px solid #E4EFF9' }}>
                  <div className="absolute -top-3 -right-3 w-12 h-12 rounded-full" style={{ background: 'rgba(70,168,249,0.07)' }} />
                  <div className="p-4 flex items-center gap-3 relative z-10">
                    <div className="w-9 h-9 rounded-[10px] flex items-center justify-center flex-shrink-0"
                         style={{ background: '#fff', border: '1.5px solid #CFE6FB' }}>
                      <Shield size={18} className="text-[#46A8F9]" />
                    </div>
                    <div>
                      <div className="text-[16px] font-bold text-[#15354F]">{t('home.hipaaSecure')}</div>
                      <div className="text-[11px] mt-px" style={{ color: '#5B7288' }}>{t('home.securityIntro') || 'Industry-leading security standards'}</div>
                    </div>
                  </div>
                </div>
                
                {/* Items */}
                <div className="divide-y divide-[#F1F5F9]">
                  <div className="flex items-start gap-3 py-3">
                    <div className="w-8 h-8 rounded-lg bg-[#E9F8FF] flex items-center justify-center flex-shrink-0">
                      <Lock size={15} className="text-[#46A8F9]" />
                    </div>
                    <div>
                      <p className="text-[13px] font-bold text-[#15354F]">{t('home.e2eEncryption')}</p>
                      <p className="text-[11px] text-[#64748B] leading-relaxed mt-0.5">{t('home.e2eDesc')}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 py-3">
                    <div className="w-8 h-8 rounded-lg bg-[#E9F8FF] flex items-center justify-center flex-shrink-0">
                      <Shield size={15} className="text-[#46A8F9]" />
                    </div>
                    <div>
                      <p className="text-[13px] font-bold text-[#15354F]">{t('home.hipaaCompliant')}</p>
                      <p className="text-[11px] text-[#64748B] leading-relaxed mt-0.5">{t('home.hipaaDesc')}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 py-3">
                    <div className="w-8 h-8 rounded-lg bg-[#E9F8FF] flex items-center justify-center flex-shrink-0">
                      <Eye size={15} className="text-[#46A8F9]" />
                    </div>
                    <div>
                      <p className="text-[13px] font-bold text-[#15354F]">{t('home.secureShareCodes')}</p>
                      <p className="text-[11px] text-[#64748B] leading-relaxed mt-0.5">{t('home.secureShareDesc')}</p>
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={() => setTrustSheet(null)}
                  className="w-full py-3 rounded-xl border-[1.5px] border-[#D8E6F0] bg-white text-[#15354F] text-[13px] font-semibold mt-3 active:bg-slate-50 transition-colors"
                >
                  {t('common.close')}
                </button>
              </div>
            )}
            
            {trustSheet === 'data' && (
              <div className="px-4 pt-1 overflow-y-auto"
                   style={{ paddingBottom: '1.5rem' /* PROD-1176: the BottomSheet panel pays the bottom inset */ }}>
                {/* v242.80.0 (TF-38) — the sheet's own header, no longer a navy
                    slab. A dark gradient card inside a white sheet is a second
                    theme inside a surface that already has one, and it is the
                    exact treatment the 1B header work just removed from four
                    screens. Same structure, ice-blue ground, navy as TEXT. */}
                <div className="rounded-[14px] overflow-hidden mb-3 relative"
                     style={{ background: 'linear-gradient(to bottom, #F4FAFF, #EAF4FE)', border: '1px solid #E4EFF9' }}>
                  <div className="absolute -top-3 -right-3 w-12 h-12 rounded-full" style={{ background: 'rgba(70,168,249,0.07)' }} />
                  <div className="p-4 flex items-center gap-3 relative z-10">
                    <div className="w-9 h-9 rounded-[10px] flex items-center justify-center flex-shrink-0"
                         style={{ background: '#fff', border: '1.5px solid #CFE6FB' }}>
                      <Lock size={18} className="text-[#46A8F9]" />
                    </div>
                    <div>
                      <div className="text-[16px] font-bold text-[#15354F]">{t('home.yourData')}</div>
                      <div className="text-[11px] mt-px" style={{ color: '#5B7288' }}>{t('home.dataIntro') || 'You own your health data'}</div>
                    </div>
                  </div>
                </div>
                
                {/* Items */}
                <div className="divide-y divide-[#F1F5F9]">
                  <div className="flex items-start gap-3 py-3">
                    <div className="w-8 h-8 rounded-lg bg-[#D5EFD3] flex items-center justify-center flex-shrink-0">
                      <User size={15} className="text-[#16A34A]" />
                    </div>
                    <div>
                      <p className="text-[13px] font-bold text-[#15354F]">{t('home.patientOwned')}</p>
                      <p className="text-[11px] text-[#64748B] leading-relaxed mt-0.5">{t('home.patientOwnedDesc')}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 py-3">
                    <div className="w-8 h-8 rounded-lg bg-[#E9F8FF] flex items-center justify-center flex-shrink-0">
                      <Sparkles size={15} className="text-[#46A8F9]" />
                    </div>
                    <div>
                      <p className="text-[13px] font-bold text-[#15354F]">{t('home.aiAssisted')}</p>
                      <p className="text-[11px] text-[#64748B] leading-relaxed mt-0.5">{t('home.aiAssistedDesc')}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 py-3">
                    <div className="w-8 h-8 rounded-lg bg-[#FEF2F2] flex items-center justify-center flex-shrink-0">
                      <X size={15} className="text-red-500" />
                    </div>
                    <div>
                      <p className="text-[13px] font-bold text-[#15354F]">{t('home.neverSold')}</p>
                      <p className="text-[11px] text-[#64748B] leading-relaxed mt-0.5">{t('home.neverSoldDesc')}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 py-3">
                    <div className="w-8 h-8 rounded-lg bg-[#E9F8FF] flex items-center justify-center flex-shrink-0">
                      <FileText size={15} className="text-[#46A8F9]" />
                    </div>
                    <div>
                      <p className="text-[13px] font-bold text-[#15354F]">{t('home.auditTrail')}</p>
                      <p className="text-[11px] text-[#64748B] leading-relaxed mt-0.5">{t('home.auditTrailDesc')}</p>
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={() => setTrustSheet(null)}
                  className="w-full py-3 rounded-xl border-[1.5px] border-[#D8E6F0] bg-white text-[#15354F] text-[13px] font-semibold mt-3 active:bg-slate-50 transition-colors"
                >
                  {t('common.close')}
                </button>
              </div>
            )}
      </BottomSheet>

      <LanguageSelector isOpen={languageOpen} onClose={() => setLanguageOpen(false)} />
    </div>
  )
}
